import { withAlpha } from "@/lib/color";
import { useEffect, useState } from "react";
import { Trophy, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * TrophyNews — "Bacheca Trofei": notizie celebrative generate dai RISULTATI
 * (finali di coppa/supercoppa + campioni di lega matematici).
 */
export default function TrophyNews({ limit = 12 }) {
    const { user } = useAuth() || {};
    const isAdmin = user?.role === "admin";
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        api.get("/trophy-news", { params: { limit } })
            .then(({ data }) => setItems(data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [limit]);

    const remove = async (id) => {
        if (!window.confirm("Eliminare questa notizia dalla bacheca trofei?")) return;
        try {
            await api.delete(`/trophy-news/${id}`);
            setItems((prev) => prev.filter((n) => n.id !== id));
        } catch {
            alert("Errore nell'eliminazione");
        }
    };

    if (loading) {
        return <div className="op-label text-t8f95a3 animate-pulse" data-testid="trophy-news-loading">// caricamento trofei…</div>;
    }
    if (items.length === 0) {
        return (
            <div className="op-card p-5 text-t8f95a3 text-sm" data-testid="trophy-news-empty">
                Ancora nessun trofeo assegnato in questa stagione. I campioni di coppe, supercoppe
                e campionati compariranno qui appena conquistano il titolo. 🏆
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="trophy-news">
            {items.map((n) => {
                const accent = n.color || "rgb(var(--tc-ffd700))";
                return (
                    <article
                        key={n.id}
                        className="op-card p-0 overflow-hidden flex flex-col border-l-4 relative"
                        style={{ borderLeftColor: accent }}
                        data-testid={`trophy-news-item-${n.id}`}
                    >
                        <div className="flex items-stretch gap-3 p-4">
                            {n.winner_logo ? (
                                <img
                                    src={n.winner_logo}
                                    alt={n.winner_name}
                                    className="w-16 h-16 rounded-lg object-contain bg-tffffff/5 border shrink-0 p-1"
                                    style={{ borderColor: `${withAlpha(accent, "55")}` }}
                                />
                            ) : (
                                <div
                                    className="w-16 h-16 rounded-lg border shrink-0 flex items-center justify-center"
                                    style={{ borderColor: `${withAlpha(accent, "55")}` }}
                                >
                                    <Trophy size={28} style={{ color: accent }} />
                                </div>
                            )}
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span
                                        className="op-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 border"
                                        style={{ color: accent, borderColor: `${withAlpha(accent, "66")}` }}
                                    >
                                        {n.kind === "league" ? "🥇 Campionato" : "🏆 Coppa"}
                                    </span>
                                    <span className="op-mono text-[10px] text-t8f95a3">
                                        {n.competition_label} · {n.season_label}
                                    </span>
                                </div>
                                <h3 className="op-heading text-lg text-tffffff mt-1.5 leading-tight">
                                    {n.title}
                                </h3>
                            </div>
                            {isAdmin && (
                                <button
                                    onClick={() => remove(n.id)}
                                    className="text-t5c6370 hover:text-tff3b30 transition-colors shrink-0 self-start"
                                    title="Elimina notizia"
                                    data-testid={`trophy-news-delete-${n.id}`}
                                >
                                    <Trash2 size={15} />
                                </button>
                            )}
                        </div>
                        <div className="px-4 pb-4 -mt-1">
                            <p className="text-tc9ccd3 text-sm leading-relaxed">{n.body}</p>
                            {n.scoreline && (
                                <div className="op-mono text-sm font-bold mt-2" style={{ color: accent }}>
                                    {n.scoreline}
                                </div>
                            )}
                            {n.scorers_text && (
                                <div className="op-mono text-[11px] text-t8f95a3 mt-1">
                                    🥅 {n.scorers_text}
                                </div>
                            )}
                        </div>
                    </article>
                );
            })}
        </div>
    );
}
